import { analyticsArticles } from './articleGroups/analytics';
import { basicsArticles } from './articleGroups/basics';
import { deploymentArticles } from './articleGroups/deployment';
import { developerArticles } from './articleGroups/developer';
import { integrationArticles } from './articleGroups/integrations';
import { workspaceArticles } from './articleGroups/workspace';

export const dashboardDocumentationPaths = [
  '/summary',
  '/profile',
  '/charts',
  '/insights',
  '/reports',
  '/notifications',
  '/devices',
  '/sites',
  '/sitegroups',
  '/campaigns',
  '/portals',
  '/pos',
  '/integrations'
];

export const documentationCategories = [
  { id: 'basics', label: 'Dashboard Basics' },
  { id: 'analytics', label: 'Analytics guides' },
  { id: 'workspace', label: 'Workspace management' },
  { id: 'deployment', label: 'Device deployment' },
  { id: 'integrations', label: 'Integrations' },
  { id: 'developer', label: 'Developer API' }
];

export const documentationNavigationSubgroups = {
  developer: [
    {
      id: 'legacy',
      label: 'Legacy',
      slugs: [
        'authentication-v1-legacy',
        'authentication-v2',
        'data-api',
        'device-status-api'
      ]
    }
  ],
  deployment: [
    { id: 'linux', label: 'Linux', slugs: ['linux-on-device'] },
    { id: 'windows', label: 'Windows', slugs: ['windows-installation'] },
    {
      id: 'balena',
      label: 'Balena',
      slugs: ['sensor-network-setup', 'balena-sensor-setup', 'vehicle-analytics-setup']
    },
    {
      id: 'brightsign',
      label: 'BrightSign',
      slugs: ['brightsign-installation', 'brightsign-provisioning']
    }
  ]
};

const documentationNavigationOrder = [
  'introduction',
  'navigating-the-dashboard',
  'sites-assets-and-devices',
  'solutions-and-data-sources',
  'filters-and-date-ranges',
  'analytics-and-insights',
  'mobile-data-in-the-dashboard',
  'exports-and-reports',
  'profile-and-site-groups',
  'managing-api-keys',
  'users-roles-and-access',
  'understanding-device-status',
  'why-data-may-be-missing-or-delayed',
  'dashboard-glossary',
  'audience-metrics',
  'traffic-metrics',
  'workspace-management',
  'campaigns-and-brand-portals',
  'web-attribution',
  'retail-media-and-sales',
  'device-notifications',
  'supported-platforms-and-cameras',
  'network-security-and-previews',
  'ip-camera-configuration',
  'vehicle-camera-positioning',
  'linux-on-device',
  'windows-installation',
  'sensor-network-setup',
  'balena-sensor-setup',
  'vehicle-analytics-setup',
  'brightsign-installation',
  'brightsign-provisioning',
  'broadsign-integration',
  'signagelive-integration',
  'dashboard-whitelabel',
  'on-device-data-stream',
  'public-api-getting-started',
  'public-api-projects',
  'public-api-sites-and-devices',
  'public-api-data-jobs',
  'authentication-v1-legacy',
  'authentication-v2',
  'data-api',
  'device-status-api'
];

const navigationPosition = new Map(documentationNavigationOrder.map((slug, index) => [slug, index]));

export const documentationExcludedSources = [
  {
    file: 'computer-configuration.pdf',
    reason: 'Superseded by the current Windows installation flow; it also contains obsolete remote-access advice.'
  },
  {
    file: 'demos.pdf',
    reason: 'Contains unverified 2020 video links rather than durable product guidance.'
  }
];

// `sources` is maintainer-only provenance and must not be rendered to customers.
const unorderedDocumentationArticles = [
  ...basicsArticles,
  ...analyticsArticles,
  ...workspaceArticles,
  ...deploymentArticles,
  ...integrationArticles,
  ...developerArticles
];

export const documentationArticles = unorderedDocumentationArticles.sort((first, second) =>
  (navigationPosition.get(first.slug) ?? Number.MAX_SAFE_INTEGER) -
  (navigationPosition.get(second.slug) ?? Number.MAX_SAFE_INTEGER));

export const getDocumentationArticle = slug => documentationArticles.find(article => article.slug === slug);

export const filterDocumentationArticles = ({ query = '', category = 'all' }) => {
  const normalizedQuery = query.trim().toLowerCase();
  return documentationArticles.filter(article => {
    const matchesCategory = category === 'all' || article.category === category;
    const searchableText = [
      article.title,
      article.summary,
      ...article.sections.flatMap(section => [
        section.title,
        ...(section.paragraphs || []),
        ...(section.bullets || []),
        ...(section.steps || []),
        ...(section.code ? [section.code] : []),
        ...(section.tables || []).flatMap(table => [table.caption, ...table.headers, ...table.rows.flat()]),
        ...(section.figures || []).flatMap(figure => [figure.alt, figure.caption])
      ]),
      ...(article.legacyNotice
        ? [
          article.legacyNotice.title,
          ...article.legacyNotice.paragraphs,
          ...Object.values(article.legacyNotice.replacement)
        ]
        : []),
      ...(article.nextArticle ? [article.nextArticle.description] : [])
    ]
      .join(' ')
      .toLowerCase();
    return matchesCategory && (!normalizedQuery || searchableText.includes(normalizedQuery));
  });
};

export const validateDocumentationArticle = article => {
  const knownSlugs = new Set(documentationArticles.map(candidate => candidate.slug));
  const articleLinks = article.sections.flatMap(section => [
    ...(section.paragraphLinks || []),
    ...(section.paragraphLink ? [section.paragraphLink] : [])
  ].map(link => ({ ...link, paragraphs: section.paragraphs || [] }))).filter(link => link.slug);
  return [
    ...article.related.filter(slug => !knownSlugs.has(slug)).map(slug => `Unknown related article: ${slug}`),
    ...articleLinks
      .filter(link => !knownSlugs.has(link.slug))
      .map(link => `Unknown article link: ${link.slug}`),
    ...articleLinks
      .filter(link => link.section && knownSlugs.has(link.slug))
      .filter(link => !getDocumentationArticle(link.slug).sections
        .some(section => section.id === link.section))
      .map(link => `Unknown article section: ${link.slug}#${link.section}`),
    ...articleLinks
      .filter(link => !link.paragraphs.some(paragraph => paragraph.includes(link.label)))
      .map(link => `Article link label not found: ${link.label}`),
    ...(article.nextArticle && !knownSlugs.has(article.nextArticle.slug)
      ? [`Unknown next article: ${article.nextArticle.slug}`]
      : []),
    ...article.dashboardLinks
      .filter(link => !dashboardDocumentationPaths.includes(link.to))
      .map(link => `Unknown Dashboard path: ${link.to}`)
  ];
};
